import React, {memo, useEffect, useRef, useState} from 'react';
import Link from 'next/link';
import Container from '@/components/atoms/Container';
import Eyebrow from '@/components/atoms/Eyebrow';
import StarRating from '@/components/atoms/StarRating';
import ArrowButton from '@/components/atoms/ArrowButton';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import {cdnUrl} from '@/util/cdnImage';
import styles from './HomeBanner.module.scss';

// MUI-free copy of Ui-components/home/home-banner. Same markup, copy, links and video
// logic; the `sx` styles live in HomeBanner.module.scss.

// All three share one 220x72 canvas, so the intrinsic size below is the same for
// each; `badgeLogoStyle` sets the rendered height.
const LOGO_INTRINSIC = {width: 220, height: 72} as const;
const clutchLogo = cdnUrl('home-page-updated-assets/clutch-badge-logo.webp');
const goodfirmsLogo = cdnUrl('home-page-updated-assets/goodfirms-badge-logo.webp');
const topDevLogo = cdnUrl('home-page-updated-assets/topdev-badge-logo.webp');

// Base narrow-layout logo height — TopDevelopers gets a few px on top of this (see below).
const LOGO_HEIGHT_NARROW = 34;

const REVIEW_BADGES = [
    {
        key: 'clutch',
        logo: clutchLogo,
        alt: 'Clutch',
        logoHeight: 44,
        logoHeightNarrow: LOGO_HEIGHT_NARROW,
        href: 'https://clutch.co/profile/debut-infotech#reviews',
        srLabel: 'Read our reviews on Clutch',
        rating: 4.8,
        note: '45 Reviews'
    },
    {
        key: 'goodfirms',
        logo: goodfirmsLogo,
        alt: 'GoodFirms',
        logoHeight: 44,
        logoHeightNarrow: LOGO_HEIGHT_NARROW,
        href: 'https://www.goodfirms.co/company/debut-infotech',
        srLabel: 'Read our reviews on GoodFirms',
        rating: 4.8,
        note: '39 Reviews'
    },
    {
        key: 'topdevelopers',
        logo: topDevLogo,
        alt: 'TopDevelopers',
        // The TopDevelopers wordmark carries more baked-in whitespace inside its canvas.
        logoHeight: 54,
        logoHeightNarrow: LOGO_HEIGHT_NARROW + 8,
        href: 'https://www.topdevelopers.co/profile/debut-infotech',
        srLabel: 'View our verified profile on TopDevelopers',
        rating: 4.9,
        note: 'Verified Profile'
    }
] as const;

const badgeLogoStyle = (height: number, narrowHeight: number = LOGO_HEIGHT_NARROW): React.CSSProperties => ({
    width: 'auto',
    height: `${height}px`,
    maxWidth: '100%',
    objectFit: 'contain',
    objectPosition: 'center',
    ['--logo-h-narrow' as string]: `${narrowHeight}px`
});

const desktopVideoStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    zIndex: 0
};

const hiddenSpanStyle: React.CSSProperties = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: '0',
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: '0'
};

const DESKTOP_MEDIA_QUERY = '(min-width: 600px)';

// First signs of a real visitor; any one of them starts the background video.
const START_EVENTS = ['pointermove', 'pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

// 1×1 transparent GIF: what phones get for the poster, since the video (and so its poster) is desktop-only.
const EMPTY_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

const HomeBanner = (props: any) => {
    const {heading, subheading, eyebrow, handleClickOpen, backgroundVideo, videoPoster, videoAlt} = props;

    // `mounted` + `isDesktopViewport` gate the MP4 bind (client + desktop only).
    const [mounted, setMounted] = useState(false);
    const [isDesktopViewport, setIsDesktopViewport] = useState(false);
    const [videoPlaying, setVideoPlaying] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const mediaQuery = globalThis.matchMedia(DESKTOP_MEDIA_QUERY);
        const updateViewport = () => setIsDesktopViewport(mediaQuery.matches);

        updateViewport();
        setMounted(true);
        mediaQuery.addEventListener('change', updateViewport);

        return () => {
            mediaQuery.removeEventListener('change', updateViewport);
        };
    }, []);

    useEffect(() => {
        if (!mounted || !isDesktopViewport || !backgroundVideo) return undefined;
        const video = videoRef.current;
        if (!video) return undefined;

        let cancelled = false;
        const onPlaying = () => setVideoPlaying(true);

        // The ONLY place that assigns video.src for the hero. Idempotent.
        const bindAndPlay = () => {
            removeStartListeners();
            if (cancelled || video.src) return;
            video.addEventListener('playing', onPlaying, {once: true});
            video.src = backgroundVideo;
            video.load();
            video.play().catch(() => undefined);
        };

        // The video is decoration, so it starts on the visitor's first interaction instead of
        // on its own during load. Until then the poster image stays put: the first screen never
        // changes by itself (Speed Index) and the 14 MB file never competes with page resources.
        function removeStartListeners() {
            START_EVENTS.forEach((type) => globalThis.removeEventListener(type, bindAndPlay));
        }
        START_EVENTS.forEach((type) => globalThis.addEventListener(type, bindAndPlay, {once: true, passive: true}));

        return () => {
            cancelled = true;
            removeStartListeners();
            video.removeEventListener('playing', onPlaying);
            video.pause();
            video.removeAttribute('src');
            video.load();
        };
    }, [mounted, isDesktopViewport, backgroundVideo]);

    return (
        <div className={styles.banner}>
            {/* Poster as a real image (the LCP element on desktop): straight from the CDN, high
                priority, found by the browser's preload scanner. <picture> keeps phones from
                downloading it at all. */}
            <picture>
                <source media={DESKTOP_MEDIA_QUERY} srcSet={videoPoster} />
                <img
                    src={EMPTY_IMAGE}
                    alt=""
                    aria-hidden="true"
                    width={1081}
                    height={608}
                    decoding="async"
                    className={`hero-video-desktop ${styles.poster}`}
                    style={desktopVideoStyle}
                    {...{fetchpriority: 'high'}}
                />
            </picture>
            <video
                ref={videoRef}
                id="hero-video"
                loop
                muted
                playsInline
                preload="none"
                className={`hero-video-desktop ${styles.video} ${videoPlaying ? styles.videoPlaying : ''}`}
                style={desktopVideoStyle}
                aria-label={videoAlt}
            />
            <div className={styles.overlay} />

            <Container className={styles.container}>
                <div className={styles.content}>
                    {eyebrow && <Eyebrow text={eyebrow} onDark />}

                    <h1 className={`font-headers ${styles.heading}`}>{heading}</h1>

                    <p className={`font-regular-text ${styles.subheading}`}>{subheading}</p>

                    <div className={styles.ctaWrapper}>
                        <ArrowButton variant="orange" className={styles.ctaButton} onClick={handleClickOpen}>
                            Discuss Your Project Idea
                        </ArrowButton>
                        <Link href="/portfolio" target="_blank" rel="noopener noreferrer" className={styles.seeWorkLink}>
                            <button type="button" className={`font-headers ${styles.seeWorkButton}`}>
                                View Our Success Stories
                            </button>
                        </Link>
                    </div>

                    <div className={styles.badgeRow}>
                        {REVIEW_BADGES.map((badge) => (
                            <a key={badge.key} href={badge.href} target="_blank" rel="noopener noreferrer" className={styles.badgeCard}>
                                <div className={styles.badgeLogoSlot}>
                                    <OptimizedImage
                                        src={badge.logo}
                                        alt={badge.alt}
                                        {...LOGO_INTRINSIC}
                                        loading="eager"
                                        sizes="140px"
                                        style={badgeLogoStyle(badge.logoHeight, badge.logoHeightNarrow)}
                                    />
                                </div>
                                <div className={styles.badgeMetaWrap}>
                                    <div className={styles.badgeRatingRow}>
                                        <span className={`font-headers ${styles.badgeRating}`}>{badge.rating.toFixed(1)}</span>
                                        <StarRating rating={badge.rating} className={styles.stars} />
                                    </div>
                                    <span className={`font-regular-text ${styles.badgeNote}`}>{badge.note}</span>
                                </div>
                                <span style={hiddenSpanStyle}>{badge.srLabel}</span>
                            </a>
                        ))}
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default memo(HomeBanner);
